/**
 * @module features/client-device-management/components/UnassignPromptModal
 * @description Responsive confirmation modal prompting the Super Admin to choose
 * between Factory Reset Wipe (`wipe` — purges client configs & WiFi bindings) and
 * Soft Archive (`archive` — retains configuration snapshot for historical audit)
 * before returning the physical device to unassigned central inventory.
 */
import * as React from "react";
import { AlertCircle, Archive, Eraser, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppError } from "@/lib/api-client";
import { AssignmentSummary, UnassignMode } from "../types";
import { useUnassignDevice } from "../api/assignmentApi";

export interface UnassignPromptModalProps {
  assignment: AssignmentSummary | null;
  open: boolean;
  onClose: () => void;
}

export function UnassignPromptModal({
  assignment,
  open,
  onClose,
}: UnassignPromptModalProps) {
  const [selectedMode, setSelectedMode] = React.useState<UnassignMode>("wipe");
  const [reason, setReason] = React.useState("");
  const [submitError, setSubmitError] = React.useState<AppError | null>(null);

  const unassignMutation = useUnassignDevice();

  React.useEffect(() => {
    if (assignment) {
      setSelectedMode("wipe");
      setReason("");
      setSubmitError(null);
    }
  }, [assignment]);

  const handleClose = (): void => {
    setSubmitError(null);
    onClose();
  };

  const handleConfirmUnassign = async (): Promise<void> => {
    if (!assignment) return;
    setSubmitError(null);
    try {
      await unassignMutation.mutateAsync({
        assignmentId: assignment.id,
        input: {
          mode: selectedMode,
          reason: reason.trim() || undefined,
        },
      });
      handleClose();
    } catch (rawErr: unknown) {
      setSubmitError(
        AppError.fromUnknown(rawErr, "Failed to unassign device from organization.")
      );
    }
  };

  const deviceTitle = assignment
    ? assignment.custom_device_name || assignment.device_name
    : "Device";

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent sheetOnMobile className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Unassign {deviceTitle}</DialogTitle>
          <DialogDescription>
            Choose how client configurations and WiFi bindings should be handled when
            detaching{" "}
            <span className="font-mono text-foreground">
              {assignment?.registered_device_id}
            </span>{" "}
            from {assignment?.client_name}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {submitError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger-soft p-3 text-xs text-danger"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
              <p>{submitError.errorDetails}</p>
            </div>
          )}

          {/* Radio Card Options: Factory Reset Wipe vs Soft Archive */}
          <div role="radiogroup" aria-label="Unassignment mode" className="space-y-2.5">
            <button
              type="button"
              role="radio"
              aria-checked={selectedMode === "wipe"}
              onClick={() => setSelectedMode("wipe")}
              className={`w-full text-left rounded-xl border p-3.5 transition-all flex items-start gap-3 ${
                selectedMode === "wipe"
                  ? "border-foreground bg-muted/40 shadow-card"
                  : "border-border/80 bg-card hover:bg-muted/20"
              }`}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-danger-soft text-danger shrink-0 mt-0.5">
                <Eraser className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground">
                  Factory Reset Wipe (Recommended for Re-deployment)
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                  Permanently purges client-specific configurations (custom name,
                  location) and deactivates WiFi credentials so no tenant data leaks.
                </p>
              </div>
            </button>

            <button
              type="button"
              role="radio"
              aria-checked={selectedMode === "archive"}
              onClick={() => setSelectedMode("archive")}
              className={`w-full text-left rounded-xl border p-3.5 transition-all flex items-start gap-3 ${
                selectedMode === "archive"
                  ? "border-foreground bg-muted/40 shadow-card"
                  : "border-border/80 bg-card hover:bg-muted/20"
              }`}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-warning-soft text-warning shrink-0 mt-0.5">
                <Archive className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground">
                  Soft Archive (Historical Audit Retention)
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                  Detaches the physical hardware back to central inventory and
                  deactivates active WiFi while preserving configuration snapshots in
                  historical audit records.
                </p>
              </div>
            </button>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="unassign-reason"
              className="text-xs font-medium text-foreground block"
            >
              Reason / Audit Note (Optional)
            </label>
            <Input
              id="unassign-reason"
              maxLength={255}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Hardware RMA swap or contract adjustment"
              className="h-11 sm:h-10 text-base sm:text-sm bg-card"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={unassignMutation.isPending}
            className="h-10 sm:h-9 touch:min-h-[44px] text-xs"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={selectedMode === "wipe" ? "destructive" : "default"}
            disabled={unassignMutation.isPending}
            onClick={() => void handleConfirmUnassign()}
            className="h-10 sm:h-9 touch:min-h-[44px] text-xs"
          >
            {unassignMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                <span>Unassigning...</span>
              </>
            ) : selectedMode === "wipe" ? (
              <span>Wipe &amp; Unassign Device</span>
            ) : (
              <span>Archive &amp; Unassign Device</span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

